import type { EmailTask, ShareRole, TaskPriority, TaskShare, TaskStatus } from '../types'
import { ApiError, authHeaders } from './auth'

const base = (import.meta.env.VITE_TASKS_API_BASE as string | undefined)?.replace(
  /\/$/,
  '',
) ?? ''

function raise(res: Response, label: string): never {
  if (res.status === 403) throw new ApiError(403, 'not_allowlisted')
  if (res.status === 401) throw new ApiError(401, 'unauthorized')
  throw new ApiError(res.status, `${label} ${res.status}`)
}

export type ListTasksParams = {
  status?: TaskStatus | 'all'
  gmail_thread_id?: string
  limit?: number
}

export async function listTasks(params: ListTasksParams = {}): Promise<EmailTask[]> {
  const q = new URLSearchParams()
  if (params.status && params.status !== 'all') q.set('status', params.status)
  if (params.gmail_thread_id?.trim()) q.set('gmail_thread_id', params.gmail_thread_id.trim())
  if (params.limit) q.set('limit', String(params.limit))
  const qs = q.toString()
  const res = await fetch(`${base}/tasks${qs ? `?${qs}` : ''}`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'list_tasks')
  const body = (await res.json()) as { tasks: EmailTask[] }
  return body.tasks
}

export type PatchTaskBody = {
  status?: TaskStatus
  priority?: TaskPriority | null
  clear_priority?: boolean
  parent_id?: string | null
  clear_parent?: boolean
  owner_email?: string | null
  clear_owner?: boolean
  assigned_by_email?: string | null
  due_at?: string | null
  extra_instructions?: string | null
  clear_extra_instructions?: boolean
}

export type ThreadMessage = {
  id: string
  direction: 'inbound' | 'outbound' | string
  from_address: string
  from_name: string | null
  subject: string
  sent_at: string | null
  body_text: string
  redacted?: boolean
}

export type TaskThread = {
  task_id: string
  gmail_thread_id: string
  inbox_address: string
  thread_access?: 'full' | 'redacted'
  messages: ThreadMessage[]
}

export async function listTaskShares(taskId: string): Promise<TaskShare[]> {
  const res = await fetch(`${base}/tasks/${taskId}/shares`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'list_shares')
  const body = (await res.json()) as { shares: TaskShare[] }
  return body.shares
}

export async function upsertTaskShare(
  taskId: string,
  grantee_email: string,
  role: ShareRole,
): Promise<TaskShare> {
  const res = await fetch(`${base}/tasks/${taskId}/shares`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ grantee_email, role }),
  })
  if (!res.ok) raise(res, 'upsert_share')
  const body = (await res.json()) as { share: TaskShare }
  return body.share
}

export async function revokeTaskShare(
  taskId: string,
  granteeEmail: string,
): Promise<void> {
  const enc = encodeURIComponent(granteeEmail)
  const res = await fetch(`${base}/tasks/${taskId}/shares/${enc}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'revoke_share')
}

export async function patchTask(id: string, body: PatchTaskBody): Promise<EmailTask> {
  const res = await fetch(`${base}/tasks/${id}`, {
    method: 'PATCH',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) raise(res, 'patch_task')
  const payload = (await res.json()) as { task: EmailTask }
  return payload.task
}

export async function patchTaskStatus(
  id: string,
  status: TaskStatus,
): Promise<EmailTask> {
  return patchTask(id, { status })
}

export async function fetchTaskThread(taskId: string): Promise<TaskThread> {
  const res = await fetch(`${base}/tasks/${taskId}/thread?limit=40`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'task_thread')
  return (await res.json()) as TaskThread
}

export type RetryAcquisitionResult = {
  retry_id: string
  root_task_id: string | null
  created_task_ids: string[]
  superseded_task_ids: string[]
  gmail_thread_id: string
}

export type RetryProgressEvent = {
  phase: string
  message?: string
  tool?: string
  title?: string | null
  turn?: number
  ok?: boolean
  detail?: string
  status?: number
  retry_id?: string
  root_task_id?: string | null
  created_task_ids?: string[]
  superseded_task_ids?: string[]
  gmail_thread_id?: string
  model?: string
}

export async function retryTaskAcquisition(
  taskId: string,
  onProgress?: (ev: RetryProgressEvent) => void,
): Promise<RetryAcquisitionResult> {
  const res = await fetch(`${base}/tasks/${taskId}/retry-acquisition`, {
    method: 'POST',
    headers: { ...(await authHeaders()), Accept: 'text/event-stream' },
  })
  if (!res.ok) raise(res, 'retry_acquisition')
  if (!res.body) throw new ApiError(502, 'retry_acquisition empty body')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let errorDetail: string | null = null
  const finish = { done: null as RetryAcquisitionResult | null }

  const handleBlock = (block: string) => {
    let event = 'message'
    const dataLines: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (!dataLines.length) return
    let payload: RetryProgressEvent
    try {
      payload = JSON.parse(dataLines.join('\n')) as RetryProgressEvent
    } catch {
      return
    }
    onProgress?.(payload)
    if (event === 'done' || payload.phase === 'done') {
      finish.done = {
        retry_id: String(payload.retry_id ?? ''),
        root_task_id: payload.root_task_id ?? null,
        created_task_ids: payload.created_task_ids ?? [],
        superseded_task_ids: payload.superseded_task_ids ?? [],
        gmail_thread_id: String(payload.gmail_thread_id ?? ''),
      }
    }
    if (event === 'error' || payload.phase === 'error') {
      errorDetail = payload.detail || 'retry_failed'
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const parts = buffer.split('\n\n')
    buffer = parts.pop() ?? ''
    for (const block of parts) {
      if (block.trim()) handleBlock(block)
    }
  }
  if (buffer.trim()) handleBlock(buffer)

  if (errorDetail) throw new ApiError(502, errorDetail)
  const done = finish.done
  if (!done?.retry_id) throw new ApiError(502, 'retry_incomplete')
  return done
}

export type ExecuteProgressEvent = {
  phase: string
  message?: string
  delta?: string
  detail?: string
  status?: number | string
  execution_id?: string
  task_id?: string
  body?: string
  model?: string
  tool?: string
  idempotent?: boolean
}

export type ExecuteDraftResult = {
  execution_id: string
  task_id: string
  body: string
  status: string
}

async function readSse(
  res: Response,
  onEvent: (event: string, payload: Record<string, unknown>) => void,
): Promise<void> {
  if (!res.body) throw new ApiError(502, 'empty_sse_body')
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const handleBlock = (block: string) => {
    let event = 'message'
    const dataLines: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (!dataLines.length) return
    try {
      onEvent(event, JSON.parse(dataLines.join('\n')) as Record<string, unknown>)
    } catch {
      /* ignore malformed */
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const parts = buffer.split('\n\n')
    buffer = parts.pop() ?? ''
    for (const block of parts) {
      if (block.trim()) handleBlock(block)
    }
  }
  if (buffer.trim()) handleBlock(buffer)
}

export async function executeTaskDraft(
  taskId: string,
  clientRequestId: string,
  onProgress?: (ev: ExecuteProgressEvent) => void,
): Promise<ExecuteDraftResult> {
  const res = await fetch(`${base}/tasks/${taskId}/execute`, {
    method: 'POST',
    headers: {
      ...(await authHeaders()),
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ client_request_id: clientRequestId }),
  })
  if (!res.ok) raise(res, 'execute')

  let errorDetail: string | null = null
  let streamed = ''
  const finish = { done: null as ExecuteDraftResult | null }

  await readSse(res, (event, raw) => {
    const payload = raw as ExecuteProgressEvent
    if (payload.phase === 'delta' && payload.delta) {
      streamed += payload.delta
      onProgress?.({ ...payload, body: streamed })
      return
    }
    onProgress?.(payload)
    if (event === 'done' || payload.phase === 'done') {
      finish.done = {
        execution_id: String(payload.execution_id ?? ''),
        task_id: String(payload.task_id ?? taskId),
        body: String(payload.body ?? streamed),
        status: String(payload.status ?? 'ready'),
      }
    }
    if (event === 'error' || payload.phase === 'error') {
      errorDetail = payload.detail || 'execute_failed'
    }
  })

  if (errorDetail) throw new ApiError(502, errorDetail)
  const done = finish.done
  if (!done?.execution_id) throw new ApiError(502, 'execute_incomplete')
  return done
}

export async function getExecution(executionId: string): Promise<{
  execution: { id: string; status: string; task_id: string }
  body: string | null
}> {
  const res = await fetch(`${base}/executions/${executionId}`, {
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'get_execution')
  return (await res.json()) as {
    execution: { id: string; status: string; task_id: string }
    body: string | null
  }
}

export async function patchExecutionBody(
  executionId: string,
  body: string,
): Promise<{ body: string }> {
  const res = await fetch(`${base}/executions/${executionId}`, {
    method: 'PATCH',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  })
  if (!res.ok) raise(res, 'patch_execution')
  return (await res.json()) as { body: string }
}

export async function sendExecution(
  executionId: string,
  body?: string,
): Promise<{ status: string; task_id: string; gmail_message_id?: string | null }> {
  const res = await fetch(`${base}/executions/${executionId}/send`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body != null ? { body } : {}),
  })
  if (!res.ok) raise(res, 'send_execution')
  return (await res.json()) as {
    status: string
    task_id: string
    gmail_message_id?: string | null
  }
}

export async function cancelExecution(executionId: string): Promise<void> {
  const res = await fetch(`${base}/executions/${executionId}/cancel`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!res.ok) raise(res, 'cancel_execution')
}

export function gmailThreadUrl(threadId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`
}
